'use client';

import '@/app/globals.css';
import '@/app/menu-styles.css';

import React, {
    ReactNode,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import {
    AuthProvider,
    useAuth,
} from '@/components/infrastructure/auth-context';
import ProtectedRoute from '@/components/infrastructure/protected-route';
import ErrorBoundary from '@/components/infrastructure/error-boundary';
import { ErrorProvider } from '@/components/infrastructure/error-context';
import {
    BreadcrumbProvider,
    useBreadcrumb,
} from '@/components/infrastructure/breadcrumb-context';
import AppBreadcrumb from '@/components/ui/breadcrumb';
import { PageTitle } from '@/components/ui/page-title';
import {
    getDynamicMenuConfig,
    settingsMenuChildren,
} from '@/config/menu-config';
import { usePathname, useSearchParams } from 'next/navigation';
import {
    App,
    Layout as AntLayout,
    Menu,
    Avatar,
    Dropdown,
    Button,
    Image,
    message,
    MenuProps,
    ConfigProvider,
    theme,
    Input,
    Skeleton,
} from 'antd';

const { Header, Content, Footer } = AntLayout;
import zhCN from 'antd/lib/locale/zh_CN';
import {
    UserOutlined,
    LogoutOutlined,
    MoreOutlined,
    SunOutlined,
    MoonOutlined,
    KeyOutlined,
    StarFilled,
    StarOutlined,
    EditOutlined,
    SafetyCertificateOutlined,
    UndoOutlined,
} from '@ant-design/icons';

import { isProtectedRoute, isPublicRoute } from '@/lib/route-utils';
import Link from 'next/link';
import {
    createUnifiedMenuItems,
    createBreadcrumbItemsNew,
    extractAllMenuItems,
} from '@/lib/menu-utils';
import { PageTabProvider } from '@/components/infrastructure/PageTabContext';

// 定义收藏菜单项接口
interface FavoriteItem {
    key: string;
    label: string;
    breadcrumbPath?: string;
}

// 定义菜单项接口
interface MenuItemType {
    key: string;
    label?: React.ReactNode;
    children?: MenuItemType[];
    hideInMenu?: boolean;
    haschildren?: boolean;
    className?: string;
    [key: string]: unknown;
}

const { Search } = Input;
// 主布局内容组件
const ContentContainer: React.FC<{
    children: ReactNode;
    themeContext: React.Context<{
        isDarkMode: boolean;
        toggleThemeMode: () => void;
    }>;
}> = ({ children, themeContext }) => {
    const [messageApi, contextHolder] = message.useMessage();

    const { profile, logout, refreshProfile } = useAuth();
    const { extraItems } = useBreadcrumb();

    // 从主题上下文获取主题状态和切换函数
    const { isDarkMode, toggleThemeMode } = React.useContext(themeContext);

    // 获取当前路径和查询参数
    const pathname = usePathname() || '/';
    const searchParams = useSearchParams();

    // 构建完整的 URL（包含查询参数）
    const fullPathname = useMemo(() => {
        const queryString = searchParams.toString();
        return queryString ? `${pathname}?${queryString}` : pathname;
    }, [pathname, searchParams]);

    // 收藏菜单项状态
    const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

    // 获取动态菜单配置
    const dynamicMenuConfig = useMemo(() => {
        return getDynamicMenuConfig({
            user: profile || undefined,
            favorites: favorites,
        });
    }, [profile, favorites]);

    // 使用useMemo缓存面包屑计算结果
    const breadcrumbItems = useMemo(() => {
        const allMenuItems = [
            ...extractAllMenuItems(dynamicMenuConfig),
            ...settingsMenuChildren,
        ];
        const baseItems = createBreadcrumbItemsNew(fullPathname, allMenuItems);

        // 追加额外的面包屑项
        if (extraItems.length > 0) {
            return [...baseItems, ...extraItems];
        }
        return baseItems;
    }, [fullPathname, dynamicMenuConfig, extraItems]);

    // 使用useMemo缓存菜单计算结果
    const menuItems = useMemo(() => {
        // 使用动态菜单配置创建统一的菜单项
        return createUnifiedMenuItems(dynamicMenuConfig);
    }, [dynamicMenuConfig]);

    // 辅助函数：获取菜单项文本
    const getMenuItemLabel = (label: React.ReactNode): string => {
        if (typeof label === 'string') {
            return label;
        } else if (React.isValidElement(label)) {
            const props = label.props as { children?: React.ReactNode };
            if (props && typeof props.children === 'string') {
                return props.children;
            } else if (props && Array.isArray(props.children)) {
                return props.children
                    .filter(
                        (child): child is string => typeof child === 'string',
                    )
                    .join(' ');
            }
        }
        return String(label).replace(/\[object Object\]/g, '');
    };

    // 辅助函数：获取面包屑路径
    const getBreadcrumbPath = (itemKey: string): string => {
        const breadcrumbItems = createBreadcrumbItemsNew(
            itemKey,
            extractAllMenuItems(dynamicMenuConfig),
        );
        return breadcrumbItems
            .map(item => {
                if (typeof item.title === 'string') {
                    return item.title;
                } else if (React.isValidElement(item.title)) {
                    try {
                        const elementProps = item.title.props as {
                            children?: string | React.ReactNode;
                        };
                        if (
                            elementProps &&
                            typeof elementProps.children === 'string'
                        ) {
                            return elementProps.children;
                        }
                    } catch {
                        // 忽略错误
                    }
                    return '未知';
                }
                return String(item.title);
            })
            .join('/');
    };

    // 收藏菜单项的处理函数
    const handleFavoriteClick = (item: MenuItemType) => {
        if (!item?.key) return;

        // 检查是否已经收藏
        const isAlreadyFavorite = favorites.some(fav => fav.key === item.key);

        if (!isAlreadyFavorite) {
            // 添加到收藏列表，包含面包屑路径
            const menuLabel = getMenuItemLabel(item.label);
            const breadcrumbPath = getBreadcrumbPath(item.key);
            setFavorites(prev => [
                ...prev,
                { key: item.key, label: menuLabel, breadcrumbPath },
            ]);
        } else {
            // 从收藏列表中移除
            setFavorites(prev => prev.filter(fav => fav.key !== item.key));
        }
    };

    // 处理退出登录
    const [logoutStatus, setLogoutStatus] = useState<
        'success' | 'error' | null
    >(null);
    const onLogoutClick = useCallback(async () => {
        const success = await logout();
        setLogoutStatus(success ? 'success' : 'error');
    }, [logout]);

    // 使用 useEffect 处理消息提示
    useEffect(() => {
        if (logoutStatus === 'success') {
            messageApi.success('退出登录成功');
            setLogoutStatus(null); // 重置状态
        } else if (logoutStatus === 'error') {
            messageApi.error('退出登录失败，请稍后重试');
            setLogoutStatus(null); // 重置状态
        }
    }, [logoutStatus, messageApi]);

    const onRefreshPermissionsClick = useCallback(async () => {
        const success = await refreshProfile();
        if (!success) {
            messageApi.error('刷新权限失败');
        }
    }, [refreshProfile, messageApi]);

    // 用户下拉菜单
    const userMenuItems: MenuProps['items'] = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <Link href="/users/profile" title={'个人资料'}>
                        个人资料
                    </Link>
                    {!profile?.profileCompleted && (
                        <Link
                            href={`/users/edit-profile?user-id=${profile?.id || ''}`}
                        >
                            <Button
                                type="text"
                                title="去编辑"
                                size="small"
                                icon={<EditOutlined />}
                                style={{ padding: 0, margin: 0 }}
                            />
                        </Link>
                    )}
                </div>
            ),
        },
        {
            key: '/change-password',
            label: (
                <Link href="/users/change-password" title={'修改密码'}>
                    修改密码
                </Link>
            ),
            icon: <KeyOutlined />,
        },
        ...(profile?.isAdmin || profile?.isDepartmentAdmin
            ? [
                  {
                      key: '/admin/approvals',
                      label: (
                          <Link href="/users/approvals" title={'审批'}>
                              授权管理
                          </Link>
                      ),
                      icon: <SafetyCertificateOutlined />,
                  },
              ]
            : []),
        ...(!profile?.isAdmin
            ? [
                  {
                      key: '/refresh-permissions',
                      label: '刷新权限',
                      icon: <UndoOutlined />,
                      onClick: onRefreshPermissionsClick,
                  },
              ]
            : []),
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '退出登录',
            onClick: onLogoutClick,
        },
    ];

    return (
        <AntLayout className="main-layout">
            {contextHolder}
            <AntLayout className="main-layout-content">
                <Header className="main-header">
                    <div className="logo-container">
                        <Image
                            src="/assets/img/logo192.png"
                            alt="Logo"
                            className="logo-image"
                        />
                        {/* <span className="logo-text">Nanny&nbsp;Goose</span> */}
                    </div>
                    <div className="header-left">
                        <Menu
                            selectedKeys={[pathname]}
                            mode="horizontal"
                            className="menu-class"
                            items={menuItems.map(menuItem => {
                                // 递归处理菜单项目，为所有没有子菜单的项添加收藏按钮
                                const processMenuItem = (
                                    item: MenuItemType | null,
                                    isTopLevel: boolean = false,
                                ): MenuItemType | null => {
                                    // 确保item是对象且有效
                                    if (!item || typeof item !== 'object')
                                        return item;
                                    // 如果菜单项设置了hideInMenu为true，则隐藏该菜单项
                                    if (item.hideInMenu) {
                                        return null;
                                    }
                                    const hasChildren =
                                        item.children &&
                                        Array.isArray(item.children) &&
                                        item.children.length > 0;

                                    // 如果有子菜单，递归处理子菜单
                                    if (hasChildren) {
                                        return {
                                            ...item,
                                            children: item
                                                .children!.map(
                                                    (child: MenuItemType) =>
                                                        processMenuItem(
                                                            child,
                                                            false,
                                                        ),
                                                )
                                                .filter(
                                                    (
                                                        child,
                                                    ): child is MenuItemType =>
                                                        child !== null,
                                                ),
                                        };
                                    }

                                    // 如果不是一级导航且没有子菜单，添加收藏按钮
                                    if (
                                        !isTopLevel &&
                                        !item.haschildren &&
                                        item.key &&
                                        !item.key.includes('favorite-')
                                    ) {
                                        // 检查当前菜单项是否已收藏
                                        const isFavorited = favorites.some(
                                            fav => fav.key === item.key,
                                        );

                                        return {
                                            ...item,
                                            label: (
                                                <span>
                                                    {item.label}
                                                    {isFavorited ? (
                                                        <StarFilled
                                                            className="favorite-button"
                                                            style={{
                                                                color: 'var(--primary-color)',
                                                            }}
                                                            onClick={(
                                                                e: React.MouseEvent,
                                                            ) => {
                                                                e.stopPropagation();
                                                                handleFavoriteClick(
                                                                    item,
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <StarOutlined
                                                            className="favorite-button"
                                                            style={{
                                                                color: 'var(--text-secondary)',
                                                            }}
                                                            onClick={(
                                                                e: React.MouseEvent,
                                                            ) => {
                                                                e.stopPropagation();
                                                                handleFavoriteClick(
                                                                    item,
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ),
                                            className: `${item.className || ''} menu-item-with-favorite-button`,
                                        };
                                    }
                                    console.log(item);

                                    return item;
                                };

                                return processMenuItem(
                                    menuItem as MenuItemType,
                                    true,
                                );
                            })}
                            style={{
                                lineHeight: '43px',
                                minWidth: 0,
                                flex: 'auto',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                borderBottom: 0,
                            }}
                        />
                    </div>
                    <div className="header-right">
                        <Search placeholder="搜索..." style={{ width: 150 }} />
                        <Button
                            type="text"
                            icon={
                                isDarkMode ? <SunOutlined /> : <MoonOutlined />
                            }
                            onClick={toggleThemeMode}
                            title={
                                isDarkMode ? '切换到亮色模式' : '切换到暗色模式'
                            }
                        />
                        <MoreOutlined className="theme-toggle-button" />
                        <Dropdown
                            menu={{
                                items: userMenuItems,
                                style: {
                                    width: 145,
                                    minWidth: 'unset', // 必须加：覆盖Ant Design Menu默认的最小宽度限制
                                    position: 'absolute',
                                    right: 0,
                                },
                            }}
                        >
                            <Button type="text" className="user-menu-button">
                                <Avatar
                                    icon={<UserOutlined />}
                                    src={profile?.avatarUrl}
                                />
                                <span
                                    className="username"
                                    onDoubleClick={async () => {
                                        if (!profile?.jobNumber) return;
                                        try {
                                            await navigator.clipboard.writeText(
                                                profile.jobNumber,
                                            );
                                            messageApi.success('工号已复制');
                                        } catch {
                                            /* empty */
                                        }
                                    }}
                                >
                                    {profile?.realname ?? 'Guest'}&nbsp;
                                    {profile?.jobNumber ?? ''}
                                </span>
                            </Button>
                        </Dropdown>
                    </div>
                </Header>
                <Content className="main-content">
                    <AppBreadcrumb
                        items={breadcrumbItems}
                        className="breadcrumb"
                    />
                    {children}
                </Content>
                <div className="footer-container">
                    <Footer className="main-footer">
                        马蹄跑服装数字化智造系统 | A-DIM © 2014-
                        {new Date().getFullYear()}
                        长春市上得科技有限公司
                    </Footer>
                    <Footer className="main-footer">
                        Matipao Apparel Digital Intelligent Manufacturing System
                    </Footer>
                </div>
            </AntLayout>
        </AntLayout>
    );
};

// 布局内容组件 - 决定哪些页面使用哪种布局
const LayoutContent: React.FC<{
    children: ReactNode;
    themeContext: React.Context<{
        isDarkMode: boolean;
        toggleThemeMode: () => void;
    }>;
}> = ({ children, themeContext }) => {
    const pathname = usePathname() || '';

    // 使用与middleware.ts一致的路由匹配逻辑
    const isPublic = isPublicRoute(pathname);
    const isProtected = isProtectedRoute(pathname);

    // 公开路由的加载状态
    const [showSkeleton, setShowSkeleton] = useState(true);

    // 设置3秒延迟后显示内容
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowSkeleton(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [pathname]);

    // 调试日志 - 记录路由匹配结果
    console.log(
        `LayoutContent - Pathname: ${pathname}, isPublic: ${isPublic}, isProtected: ${isProtected}`,
    );

    return (
        <>
            {/* 页面标题组件，用于动态设置浏览器标签页标题 */}
            <PageTitle />

            {isPublic ? (
                // 对于公开路由页面，显示3秒骨架屏后渲染内容
                showSkeleton ? (
                    <div className="m24">
                        <Skeleton active paragraph={{ rows: 10 }} />
                    </div>
                ) : (
                    children
                )
            ) : // 对于受保护路由页面，应用保护路由和主布局
            // 对于既不是公开也不是受保护的路由，让Next.js自动处理（如显示not-found页面）
            isProtected ? (
                <ProtectedRoute>
                    <ContentContainer themeContext={themeContext}>
                        {children}
                    </ContentContainer>
                </ProtectedRoute>
            ) : (
                children
            )}
        </>
    );
};

// 创建主题上下文，放在组件外部避免每次渲染都创建新的上下文
const ThemeContext = React.createContext<{
    isDarkMode: boolean;
    toggleThemeMode: () => void;
}>({
    isDarkMode: false,
    toggleThemeMode: () => {},
});
export { ThemeContext };
export default function Layout({ children }: { children: ReactNode }) {
    // 将isDarkMode改为状态变量，确保能响应主题变化
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        // 只在客户端执行
        const isDark =
            localStorage.getItem('theme') === 'dark' ||
            (!localStorage.getItem('theme') &&
                document.documentElement.classList.contains('dark')) ||
            (!localStorage.getItem('theme') &&
                window.matchMedia('(prefers-color-scheme: dark)').matches);

        setIsDarkMode(isDark);

        // 同时更新 DOM 的 class
        const cl = document.documentElement.classList;
        if (isDark) {
            cl.remove('light');
            cl.add('dark');
        } else {
            cl.remove('dark');
            cl.add('light');
        }
    }, []);

    // 主题切换函数 - 放在顶层组件中，确保能更新ConfigProvider
    const toggleThemeMode = useCallback(() => {
        if (!mounted) return;
        const cl = document.documentElement.classList;
        setIsDarkMode(prevMode => {
            const newMode = !prevMode;
            cl.remove(!prevMode ? 'light' : 'dark');
            cl.add(!prevMode ? 'dark' : 'light');
            // 持久化主题偏好到本地存储
            localStorage.setItem('theme', newMode ? 'dark' : 'light');
            return newMode;
        });
    }, [mounted]);

    // 使用useMemo缓存themeConfig对象，避免不必要的重新创建
    const themeConfig = useMemo(
        () => ({
            algorithm: isDarkMode
                ? theme.darkAlgorithm
                : theme.defaultAlgorithm,
            token: isDarkMode
                ? {
                      colorBgLayout: '#141414',
                      colorBgElevated: '#1f1f1f',
                      colorBorder: 'rgba(255, 255, 255, 0.15)',
                      colorPrimary: '#40a9ff',
                  }
                : {
                      colorBgLayout: '#ffffff',
                      colorBgElevated: '#ffffff',
                      colorBorder: '#d9d9d9',
                      colorPrimary: '#1890ff',
                  },
            components: {
                Table: {
                    borderColor: isDarkMode ? '#f0f0f033' : '#d9d9d988',
                },
            },
        }),
        [isDarkMode],
    );

    return (
        <html lang="zh">
            <body className="layout-body">
                {mounted && (
                    <App>
                        <ErrorProvider>
                            <ErrorBoundary>
                                <BreadcrumbProvider>
                                    <AuthProvider>
                                        <ThemeContext.Provider
                                            value={{
                                                isDarkMode,
                                                toggleThemeMode,
                                            }}
                                        >
                                            <PageTabProvider>
                                                <ConfigProvider
                                                    theme={themeConfig}
                                                    locale={zhCN}
                                                    input={{
                                                        autoComplete: 'off',
                                                    }}
                                                >
                                                    <LayoutContent
                                                        themeContext={
                                                            ThemeContext
                                                        }
                                                    >
                                                        {children}
                                                    </LayoutContent>
                                                </ConfigProvider>
                                            </PageTabProvider>
                                        </ThemeContext.Provider>
                                    </AuthProvider>
                                </BreadcrumbProvider>
                            </ErrorBoundary>
                        </ErrorProvider>
                    </App>
                )}
            </body>
        </html>
    );
}
